import { useEffect, useRef, useState } from 'react';
import type { CheckoutStep, CheckoutStepStatus } from '@t/checkout.types';
import { useQueryClient } from '@tanstack/react-query';
import { useInstallationDetails } from '@hooks/useInstallOptions';
import { useReleasePortReservation } from '@hooks/useReleasePortReservation';
import { useReleaseSlot } from '@hooks/useReleaseSlot';
import { useCheckoutStore } from '@store/checkoutStore';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';

type Params = {
  sessionId: string | null;
  cartId: number | undefined;
  reconciledStepStatuses: Record<CheckoutStep, CheckoutStepStatus>;

  cartHasInstallationDetails: boolean;
  amsQualifiedAddressId: string | null | undefined;
};

export const useInstallationDetailsLifecycle = ({
  sessionId,
  cartId,
  reconciledStepStatuses,
  cartHasInstallationDetails,
  amsQualifiedAddressId,
}: Params) => {
  const queryClient = useQueryClient();

  const [manualFetch, setManualFetch] = useState(false);

  const resumeRoute = useQualificationFlowStore((s) => s.resumeRoute);

  const installationDetailsResult = useInstallationDetails(sessionId, { enabled: manualFetch && !resumeRoute });

  const enterInstallationEditAction = useCheckoutStore((s) => s.enterInstallationEdit);

  const slotRelease = useReleaseSlot();
  const portRelease = useReleasePortReservation();

  const enableFetch = (): void => setManualFetch(true);

  const releaseSlotAndEnter = async (): Promise<void> => {
    if (cartId) {
      await slotRelease
        .releaseAsync(cartId)
        .catch((err) => console.warn('[InstallationLifecycle] Slot release failed:', err));
    }
    enterInstallationEditAction();
  };

  const enterInstallationEdit = (): void => {
    void releaseSlotAndEnter();
  };

  const installStatus = reconciledStepStatuses.installation;

  const releasesNeededRef = useRef(false);
  const portReleaseRequiredRef = useRef(false);

  const didTriggerMountResetRef = useRef(false);
  useEffect(() => {
    if (didTriggerMountResetRef.current) return;
    if (cartId === undefined) return;

    if (installStatus !== 'completed' || !cartHasInstallationDetails) {
      didTriggerMountResetRef.current = true;
      return;
    }

    didTriggerMountResetRef.current = true;
    releasesNeededRef.current = true;

    slotRelease.release(cartId);
    if (amsQualifiedAddressId) {
      portReleaseRequiredRef.current = true;
      portRelease.release(cartId);
    }

    enterInstallationEditAction();
  }, [cartId, installStatus, cartHasInstallationDetails, amsQualifiedAddressId]);

  const didFireOnActiveRef = useRef(false);
  useEffect(() => {
    if (installStatus !== 'active') {
      didFireOnActiveRef.current = false;
      return;
    }

    if (releasesNeededRef.current) {
      const portGateOpen = !portReleaseRequiredRef.current || portRelease.isSuccess;
      if (!slotRelease.isSuccess || !portGateOpen) return;
    }

    if (didFireOnActiveRef.current) return;
    didFireOnActiveRef.current = true;

    if (!manualFetch) {
      setManualFetch(true);
    } else {
      queryClient.invalidateQueries({ queryKey: ['installationDetails', sessionId] });
    }
  }, [installStatus, slotRelease.isSuccess, portRelease.isSuccess]);

  return {
    installationDetailsResult,
    enableFetch,
    enterInstallationEdit,
  };
};
